import { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EchoContext from 'context/EchoContext';
import authSlice from 'store/modules/auth';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';

/**
 * Hook to listen for plan request approval/rejection events via WebSocket.
 * Subscribes to the private users.{id} channel.
 * On approval: refreshes user state (membership_status, platform).
 * On rejection: shows an error snackbar.
 */
function usePlanRequestListener() {
  const { t } = useTranslation(['notifications']);
  const dispatch = useDispatch();
  const echoContext = useContext(EchoContext);
  const echo = echoContext?.echo || null;
  const userId = useSelector((state) => state.auth.user?.id);
  const channelRef = useRef(null);
  const previousUserIdRef = useRef(userId);

  useEffect(() => {
    if (previousUserIdRef.current !== userId && previousUserIdRef.current !== null) {
      if (channelRef.current && echo && typeof echo.leave === 'function') {
        try {
          channelRef.current.stopListening('.plan.request.approved');
          channelRef.current.stopListening('.plan.request.rejected');
          channelRef.current = null;
        } catch (_) {
          // silent cleanup
        }
      }
    }
    previousUserIdRef.current = userId;
  }, [userId, echo]);

  useEffect(() => {
    if (!echo || !userId) return undefined;
    if (channelRef.current) return undefined;
    if (!echo.private || typeof echo.private !== 'function') return undefined;

    let channel;
    try {
      channel = echo.private(`users.${userId}`);
      channelRef.current = channel;
    } catch (_) {
      return undefined;
    }

    channel.listen('.plan.request.approved', () => {
      dispatch(authSlice.actions.me('platform membership_status membership_days_left available_sessions'));
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({
        key: `plan-approved-${Date.now()}`,
        variant: 'success',
        message: t('planRequest.approved', { ns: 'notifications' }),
        options: { persist: false, autoHideDuration: 8000 }
      })));
    });

    channel.listen('.plan.request.rejected', () => {
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({
        key: `plan-rejected-${Date.now()}`,
        variant: 'error',
        message: t('planRequest.rejected', { ns: 'notifications' }),
        options: { persist: false, autoHideDuration: 8000 }
      })));
    });

    return () => {
      if (channelRef.current) {
        try {
          channelRef.current.stopListening('.plan.request.approved');
          channelRef.current.stopListening('.plan.request.rejected');
          channelRef.current = null;
        } catch (_) {
          // silent cleanup
        }
      }
    };
  }, [echo, userId, dispatch, t]);
}

export default usePlanRequestListener;
